import {
	Card,
	CombinationPreview,
	MicroLabel,
	type CombinationPreviewProps,
} from '@codezo/quote-detail';
import type { ReactNode } from 'react';
import type { Tone } from './types';

/**
 * How a paid row's status is coloured.
 *
 * Text only — no tinted ground. See the row for why a pill is wrong in a card
 * whose subject is money; the tone still carries "settled" apart from "part
 * paid" without taking the weight a badge would.
 */
const STATUS_TONE: Record<Tone, string> = {
	neutral: 'text-text-4',
	info: 'text-status-info',
	success: 'text-status-success',
	warn: 'text-status-warn',
	danger: 'text-status-danger',
	accent: 'text-accent',
};

/** One payment that has already been made, shown against the order total. */
export interface OrderPaymentRow {
	key: string;
	/** "Sample payment". */
	label: string;
	status: { label: string; tone: Tone };
	/** What it took off the order, rendered as a deduction. */
	amount: ReactNode;
	/** "Credited toward this order". */
	note?: ReactNode;
}

export interface OrderTotalProps extends Omit<CombinationPreviewProps, 'title' | 'hint'> {
	/** Order number, beside the heading. */
	reference?: ReactNode;
	/** Overrides the heading. */
	label?: string;
	/**
	 * What has been paid against this order, and what is left.
	 *
	 * ── Payments are DEDUCTIONS, not a second set of totals ──
	 *
	 * This block went through several shapes that all failed the same way: they
	 * restated the order as products-and-samples buckets, which is a second
	 * accounting of the same money and left the reader holding two models at once.
	 * A prepaid sample nets to zero in what the order is WORTH and is charged in
	 * full in what gets PAID, so the two views disagree on exactly one line and no
	 * amount of labelling bridged it.
	 *
	 * So money moved is shown the way a statement shows it: the order total, minus
	 * each payment made, leaving what is due. One number going down, which is the
	 * question anyone actually arrives with.
	 *
	 * ── And why paying happens HERE ──
	 *
	 * The flow spine is about work: sampling, production, shipping. Paying is not
	 * work, it is money, and it used to be split across three places — a setup row,
	 * a per-item row, and this card — so no single view answered "what do I owe".
	 * The card that states the total is the one that should take the payment.
	 */
	payments?: {
		/** What has already been paid. Empty on an order nobody has paid yet. */
		paid: OrderPaymentRow[];
		/** The figure this all exists to produce. */
		remaining: ReactNode;
		/**
		 * The arithmetic, spelled out — "€301.34 order total − €250.83 paid =
		 * €50.51 remaining".
		 *
		 * Every part is a field; the sentence only puts them in a row. It is here
		 * because every version of this block without it drew the same question:
		 * do these numbers add up? Showing the sum is cheaper than being asked.
		 */
		equation?: ReactNode;
		/** The pay control. Absent on a surface that cannot pay — the admin's. */
		action?: ReactNode;
	};
}

/**
 * What the order is worth, and where the money against it stands.
 *
 * ── It IS the quote's combination panel ──
 *
 * Not a lookalike — `CombinationPreview`, wrapped in a card and a heading. The
 * two answer the same question about the same money, one before the client
 * agrees to it and one after, so an order that totalled up differently from the
 * quote it came from would be read as a discrepancy rather than a design.
 * Everything that made that panel right is inherited: the products listed beside
 * their totals, the charges grouped by the thing the money is about, the column
 * building UP to the Total, and margin sitting past it behind its own rule.
 *
 * The panel splits into two halves where there is room and stacks where it
 * doesn't, off its own container width rather than the viewport — which is what
 * lets it work in a 320px rail and full width without a breakpoint either way.
 *
 * ── Why payment state IS here now ──
 *
 * It was deliberately elsewhere, because what has been paid was counted over
 * products and samples net of VAT while this column ends on a gross total
 * including delivery — two bases that cannot share a card without one looking
 * like a remainder of the other. That is being fixed at source, and "what is this
 * worth" and "what is still owed" are the same question asked a beat apart: a
 * reader who wants one is standing in front of the other.
 *
 * It sits BELOW the Total, behind its own rule, for the reason margin does: it is
 * not a charge and must not appear to feed the column that sums.
 *
 * Invoices stay out, for a different reason that has not changed — a transaction
 * is a historical fact in the currency it settled in, while every figure here
 * converts with the currency selector.
 */
export function OrderTotal({
	reference,
	label = 'Order total',
	payments,
	...preview
}: OrderTotalProps) {
	return (
		<Card>
			<div className="flex items-baseline gap-2 border-b border-line-1 px-4 py-3">
				<MicroLabel>{label}</MicroLabel>
				{reference && (
					<span className="ml-auto truncate font-mono text-2xs text-text-4">
						{reference}
					</span>
				)}
			</div>

			<CombinationPreview {...preview} />

			{payments && (
				/* Scaled to the mock, not miniaturised from it. The design was drawn
				   at roughly three times the width this rail actually has, so every
				   element reproduced at its own size read as clutter — which is what
				   made a box around the figure look like noise rather than a panel.
				   What the mock actually carries is AIR: a step more type, roughly
				   double the padding, and a full-height control. That survives the
				   narrower column; the pixel sizes do not. */
				<div className="border-t border-line-2 bg-surface-sunken px-4 py-4">
					<MicroLabel className="mb-3 block">Payment summary</MicroLabel>

					{payments.paid.map((row) => (
						<div key={row.key} className="pb-2.5">
							{/* Label and amount, two columns, NO wrap.
							    All three used to share one `flex-wrap` row with the amount
							    on `ml-auto`: whenever the label and its pill ran wide the
							    amount dropped to a line of its own, so "Sample payment"
							    kept its figure on the row and "Production payment" did
							    not — two rows of one kind laid out two ways, by accident
							    of string length. */}
							<div className="flex items-baseline gap-3">
								<span className="min-w-0 flex-1 text-sm text-text-2">
									{row.label}
								</span>
								{/* No minus sign.
								    It carried one, on the ledger convention that a payment
								    against an invoice is a credit. That needs the positive
								    line in the SAME column to deduct from, and there is
								    none: the order total sits in the block above, behind a
								    border, in another section. So the sign pointed at
								    something outside its own block and read as money taken
								    away — a refund — rather than money received.
								    The subtraction is stated literally one line below, in
								    the equation. It does not need saying twice, once in a
								    glyph. */}
								<span className="shrink-0 font-mono text-sm tabular-nums text-text-2">
									{row.amount}
								</span>
							</div>
							{/* Status as a WORD, not a pill.
							    A pill is a tinted, uppercased mark, and two of them stacked
							    in a block this short were the loudest thing in a card whose
							    subject is money — each one fighting the figure beside it.
							    Worse, they describe a different thing from the number they
							    sat under: the figure is money already RECEIVED, the status
							    is whether that bucket is settled. That second fact is
							    already stated, better, by "Remaining to pay" below.
							    So it keeps its tone and gives up its ground, and joins the
							    note as one quiet line. The deliveries card keeps its pills
							    for the opposite reason: there, state IS the subject. */}
							<div className="mt-0.5 text-2xs text-text-4">
								<span className={STATUS_TONE[row.status.tone]}>
									{row.status.label}
								</span>
								{row.note && <> · {row.note}</>}
							</div>
						</div>
					))}

					{/* No box, and none is possible here: this block is
					    `surface-sunken`, which the palette defines as cut INTO a
					    surface. A panel on top of it has to be lighter, which floats a
					    white slab in a recess; the only tint darker than sunken is
					    `fill`, one step away and invisible at this size. So the figure
					    is separated the way the rest of the card separates things —
					    a rule and the type scale. */}
					<div
						className={
							'flex flex-wrap items-center justify-between gap-3' +
							(payments.paid.length > 0 ? ' mt-1 border-t border-line-2 pt-3' : '')
						}
					>
						<span className="text-base font-medium text-text-1">Remaining to pay</span>
						<span className="font-mono text-2xl font-medium -tracking-[0.01em] text-text-1">
							{payments.remaining}
						</span>
					</div>

					{payments.action && <div className="mt-3">{payments.action}</div>}

					{payments.equation && (
						<div className="mt-3 text-xs leading-relaxed text-text-4">
							{payments.equation}
						</div>
					)}
				</div>
			)}
		</Card>
	);
}
